import { AnimatePresence, motion } from 'motion/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FAVORITES, FILTER_TAGS } from '@pages/Trade/utils/constants'

import { Chip } from '@ui/components/Chip/Chip'
import { Input } from '@ui/components/Input/Input'
import { CloseIcon } from '@ui/icons/Close.icon'
import { SearchIcon } from '@ui/icons/Search.icon'
import { FilterHorizontalScroll } from '@ui/v2/TableFilter/FilterHorizontalScroll'

import { useWindowResize } from '@utils/domUtils'

import type { WatchlistModel } from '@model/DxTrade.types'

import { Box } from '@shared/components/Box'

import { ManageWatchlistsDropdown } from './Watchlists/ManageWatchlistsDropdown'

import styles from './Filters.module.scss'

type FiltersProps = {
  searchValue?: string
  activeTag: string | null
  privateWatchlists?: WatchlistModel[]
  popularWatchlist?: WatchlistModel
  ownerExternalId?: string
  accountCode?: string
  onWatchlistCreated?: (name: string) => void
  onWatchlistEdited?: (newName: string) => void
  onWatchlistDeleted?: (name: string) => void
  showSearch?: boolean
  showManageDropdown?: boolean
  isSearchOpen?: boolean
  onSearchChange?: (value: string) => void
  onToggleTag: (tag: string) => void
}

const Filters = ({
  searchValue = '',
  activeTag,
  privateWatchlists = [],
  popularWatchlist,
  ownerExternalId,
  accountCode,
  onWatchlistCreated,
  onWatchlistEdited,
  onWatchlistDeleted,
  onSearchChange,
  onToggleTag,
  showSearch = true,
  showManageDropdown = true,
  isSearchOpen = false,
}: FiltersProps) => {
  const { t } = useTranslation()

  const isMobile = useWindowResize()
  const showClearIcon = searchValue.length > 0 ? CloseIcon : undefined
  const existingWatchlistNames = privateWatchlists
    .map((w) => w.name)
    .filter((n): n is string => !!n)

  const shouldRenderSearchInline = !isMobile || !isSearchOpen

  const sortedNonFavoriteWatchlists = useMemo(
    () =>
      privateWatchlists
        .filter((w) => w.name !== FAVORITES)
        .sort((a, b) => (a.orderValue ?? 0) - (b.orderValue ?? 0)),
    [privateWatchlists]
  )

  const favoritesWatchlist = useMemo(
    () => privateWatchlists.find((w) => w.name === FAVORITES),
    [privateWatchlists]
  )

  const isSearchActive = searchValue.length > 0
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const isExpanded = isSearchActive || isSearchFocused || (isMobile && isSearchOpen)

  return (
    <Box className={styles.filters} flex align={isMobile ? 'start' : 'center'} gap='x8'>
      {showSearch && shouldRenderSearchInline && (
        <motion.div
          className={styles.searchField}
          animate={isMobile ? undefined : { maxWidth: isExpanded ? 500 : 328 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
        >
          <Input
            label=''
            fullWidth
            placeholder={t('Instruments.Search instruments')}
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            variant='search'
            size='lg'
            leftIcon={SearchIcon}
            rightIcon={showClearIcon}
            onEndIconClick={() => onSearchChange?.('')}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
          />
        </motion.div>
      )}

      <AnimatePresence initial={false}>
        {!isExpanded && (
          <motion.div
            key='filters'
            className={styles.filtersMotionWrapper}
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Box flex align='center' gap='x8' minWidth='0'>
              {showManageDropdown && (
                <ManageWatchlistsDropdown
                  ownerExternalId={ownerExternalId}
                  accountCode={accountCode}
                  existingNames={existingWatchlistNames}
                  favoritesWatchlist={favoritesWatchlist}
                  nonFavoriteWatchlists={sortedNonFavoriteWatchlists}
                  onCreated={onWatchlistCreated}
                  onEdited={onWatchlistEdited}
                  onDeleted={onWatchlistDeleted}
                />
              )}

              <div className={styles.scrollWrapper}>
                <FilterHorizontalScroll>
                  <Box flex gap='x8' p='x2'>
                    {popularWatchlist?.name ? (
                      <Chip
                        size='sm'
                        selected={activeTag === popularWatchlist.name}
                        onClick={() => onToggleTag(popularWatchlist.name ?? '')}
                      >
                        {t('Instruments.Popular')}
                      </Chip>
                    ) : (
                      <Chip size='sm' selected={!activeTag} onClick={() => onToggleTag('All')}>
                        {t('Instruments.All')}
                      </Chip>
                    )}
                    <Chip
                      size='sm'
                      selected={activeTag === FAVORITES}
                      onClick={() => onToggleTag(FAVORITES)}
                    >
                      {t(`Instruments.${FAVORITES}`)}
                    </Chip>
                    {sortedNonFavoriteWatchlists.map((watchlist) => (
                      <Chip
                        key={watchlist.id}
                        size='sm'
                        selected={activeTag === watchlist.name}
                        onClick={() => onToggleTag(watchlist.name ?? '')}
                      >
                        {watchlist.name}
                      </Chip>
                    ))}
                    {FILTER_TAGS.filter((tag) => tag !== 'All' && tag !== FAVORITES).map((tag) => {
                      const isActive = activeTag === tag

                      return (
                        <Chip
                          key={tag}
                          size='sm'
                          selected={isActive}
                          onClick={() => onToggleTag(tag)}
                        >
                          {t(`Instruments.${tag}`)}
                        </Chip>
                      )
                    })}
                  </Box>
                </FilterHorizontalScroll>
              </div>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

export default Filters
